import {
  Box,
  Button,
  Column,
  Columns,
  DropdownButton,
  DropdownButtonContext,
  EmptyState,
  FlexBox,
  IconButton,
  List,
  Text,
  Tooltip,
} from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { ampli } from 'lib-shared-tracking';
import moment from 'moment-timezone';
import { memo, useCallback, useMemo } from 'react';
import { useMount } from 'react-use';

import type { EmailDraftV2 } from 'apis/userStorage';
import { appActions } from 'app/app.actions';
import { AppLink } from 'components/appLink';
import { CommandKeybindingsTooltipContent } from 'components/commandKeybindingsTooltipContent';
// eslint-disable-next-line no-restricted-imports
import { composeEmailActions } from 'components/shared/composeEmail/actions/composeEmailActions';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useServerStorage } from 'utils/useServerStorage/useServerStorage';

import { TopbarButton } from '../topbarButton/';

import styles from './emailTopbarButton.module.scss';
import { EmailTopbarButtonOld } from './emailTopbarButtonOld';

const numberOfVisibleDrafts = 5;

type EmailTopbarButtonProps = {
  alignCenter?: boolean;
};

export const EmailTopbarButton = memo(function EmailTopbarButton({
  alignCenter,
}: EmailTopbarButtonProps): JSX.Element {
  const { emailComposeV2: isV2 } = useFeatureFlag(['email-compose-v2']);

  if (!isV2) {
    return <EmailTopbarButtonOld alignCenter={alignCenter} />;
  }

  return <EmailTopbarButtonContent alignCenter={alignCenter} />;
});

// When removing the old modal, inline this component's body into EmailTopbarButton above.
const EmailTopbarButtonContent = memo(function EmailTopbarButtonContent({
  alignCenter,
}: EmailTopbarButtonProps): JSX.Element {
  const dispatch = useAppDispatch();
  const { data: userStorage, set: setUserStorage } = useServerStorage('user');

  // TODO: Replace composeEmailGetAllFrom with integrationsApi.endpoints.readEmailIntegrations
  // (RTK Query, already exists). The data currently lands in state.composeEmail.fromEmails which
  // ConversationForm reads for its "No email integration found" warning. To migrate:
  // 1. Use useQuery(readEmailIntegrations) here or in ConversationFormHookAdapter
  // 2. Pass the result as a prop to ConversationForm, removing its dependency on the Redux slice
  // 3. Delete the fromEmails/getAllFrom state from the composeEmail reducer
  useMount(() => {
    dispatch(composeEmailActions.composeEmailGetAllFrom());
  });

  const allDrafts = useMemo(
    () => userStorage.drafts?.email ?? [],
    [userStorage.drafts?.email],
  );

  const visibleDrafts = useMemo(
    (): EmailDraftV2[] =>
      allDrafts.slice(-numberOfVisibleDrafts).reverse() as EmailDraftV2[],
    [allDrafts],
  );

  const notVisibleDraftsNumber = allDrafts.length - visibleDrafts.length;

  const onToggle = useCallback((isOpen: boolean) => {
    if (isOpen) ampli.topbarEmailOpened();
  }, []);

  const openCompose = useCallback(
    (draft?: EmailDraftV2) => {
      dispatch(
        appActions.showModal({
          modal: {
            type: 'app:composeEmail',
            arg: draft ? { draft } : {},
          },
        }),
      );
    },
    [dispatch],
  );

  const removeDraft = useCallback(
    (draft: EmailDraftV2) => {
      setUserStorage({
        drafts: (storage) => {
          if (!storage.email) return;
          const idx = storage.email.findIndex(
            (d) => d.timestamp === draft.timestamp,
          );
          if (idx !== -1) storage.email.splice(idx, 1);
        },
      });
    },
    [setUserStorage],
  );

  const tooltip = (
    <CommandKeybindingsTooltipContent commandName="conversation:composeNewEmail">
      New e-mail
    </CommandKeybindingsTooltipContent>
  );

  return (
    <DropdownButton
      icon="bell"
      className={styles.root}
      onToggle={onToggle}
      noArrow
      noStyle
      dropdown={
        <DropdownButtonContext.Consumer>
          {({ closeDropdown }) => (
            <Columns>
              <Column
                className={classnames(
                  { [styles.fullWidthContent]: alignCenter },
                  styles.content,
                )}
              >
                <Column.Row slottable borderBottom>
                  <Column.Slot placement="start">
                    <Text.Heading>Email</Text.Heading>
                  </Column.Slot>

                  <Column.Slot placement="end">
                    <Tooltip content={tooltip}>
                      <Button
                        type="button"
                        variant="secondary"
                        style={{ width: '100%' }}
                        onClick={() => openCompose()}
                      >
                        Compose
                      </Button>
                    </Tooltip>
                  </Column.Slot>
                </Column.Row>

                <Column.Row
                  scrollable
                  noHorizontalPadding
                  noVerticalPadding={visibleDrafts.length > 0}
                >
                  {visibleDrafts.length ? (
                    <Box padding="200 0">
                      <List className={styles.list}>
                        {visibleDrafts.map((draft, index) => (
                          <List.Item
                            key={index}
                            withHoverEffect={false}
                            title={
                              <FlexBox
                                flexDirection="column"
                                padding="0 0 0 250"
                              >
                                <Text.Paragraph
                                  variant="2"
                                  as="em"
                                  color="secondary"
                                >
                                  {draft.subject || 'Unknown'} (Unsent draft)
                                </Text.Paragraph>

                                <Text.Paragraph
                                  as="div"
                                  variant="4"
                                  color="tertiary"
                                >
                                  {moment(draft.timestamp).fromNow()}
                                </Text.Paragraph>
                              </FlexBox>
                            }
                            contentRight={
                              <FlexBox>
                                <Tooltip content="Edit">
                                  <IconButton
                                    icon="pen"
                                    variant="tertiary"
                                    onClick={() => openCompose(draft)}
                                  />
                                </Tooltip>

                                <Tooltip content="Delete">
                                  <IconButton
                                    icon="trash"
                                    variant="tertiary"
                                    onClick={() => removeDraft(draft)}
                                  />
                                </Tooltip>
                              </FlexBox>
                            }
                          />
                        ))}
                      </List>
                    </Box>
                  ) : (
                    <EmptyState
                      title="No email drafts"
                      description="You haven't saved any email drafts yet."
                      icon={null}
                    />
                  )}
                </Column.Row>

                <Column.Row slottable noMinHeight borderTop>
                  <Column.Slot placement="center">
                    <AppLink path="/drafts" onClick={closeDropdown}>
                      {notVisibleDraftsNumber > 0
                        ? `See all drafts (${notVisibleDraftsNumber} more)`
                        : 'See all drafts'}
                    </AppLink>
                  </Column.Slot>
                </Column.Row>
              </Column>
            </Columns>
          )}
        </DropdownButtonContext.Consumer>
      }
      dropdownProps={{
        className: classnames({
          [styles.dropdownMenuAlignCenter]: alignCenter,
        }),
      }}
      data-testid="compose-email-dropdown"
    >
      <TopbarButton
        icon="mail"
        tooltip={tooltip}
        notificationIndicatorTooltip={
          allDrafts.length > 0 ? 'You have unused email drafts' : undefined
        }
        notificationIndicatorClassName={styles.notificationIndicator}
        showNotificationIndicator={allDrafts.length > 0}
      />
    </DropdownButton>
  );
});
